import axios from 'axios'
import { getAuthToken } from '@/services/auth'

const getDefaultState = () => {
    return {
        subscriptionStatus: 'unknown', // 'unknown', 'subscribed', 'unsubscribed', 'error'
        endpointArn: null,
        platform: null,
        isConfigured: false,
        configurationIssues: [],
    }
};

const state = getDefaultState();

const getters = {
    getSubscriptionStatus: state => state.subscriptionStatus,
    getEndpointArn: state => state.endpointArn,
    getPlatform: state => state.platform,
    isConfigured: state => state.isConfigured,
    getConfigurationIssues: state => state.configurationIssues,
    isSubscribed: state => state.subscriptionStatus === 'subscribed' && state.endpointArn,
}

const actions = {

    async checkPushNotificationStatus({commit, dispatch, rootState}) {
        // Try to refresh the token first to ensure we have a valid token
        try {
            await getAuthToken();
        } catch (error) {
            console.error('Failed to refresh token before checking push status:', error);
            // Continue anyway and let the API call handle any auth issues
        }

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            };

            // Add a timestamp to prevent browser/CDN caching
            const timestamp = new Date().getTime();
            config.params = { _t: timestamp };

            const response = await axios.get(`/v2/push-notifications/status`, config);

            commit('SET_CONFIGURATION_STATUS', {
                isConfigured: response.data.configured,
                issues: response.data.issues || [],
                platforms: response.data.platforms || {}
            });

            return response.data;
        }
        catch (err) {
            console.error('Error checking push notification status:', err);
            commit('SET_CONFIGURATION_STATUS', {
                isConfigured: false,
                issues: ['Failed to check configuration'],
                platforms: {}
            });
            
            dispatch('notifications/addNotification', {
                type: 'error',
                message: 'Failed to check push notification configuration'
            }, {root: true});
            throw err;
        }
    },

    async subscribeToPushNotifications({commit, dispatch, rootState}, payload) {
        // Try to refresh the token first to ensure we have a valid token
        try {
            await getAuthToken();
        } catch (error) {
            console.error('Failed to refresh token before subscribing:', error);
            // Continue anyway and let the API call handle any auth issues
        }

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            };

            // Handle both FCM token format and legacy subscription format
            let requestBody;
            
            if (payload.fcmToken) {
                // New FCM format - exactly what your backend expects
                requestBody = {
                    fcmToken: payload.fcmToken
                };
                console.log('📱 Sending FCM token to backend:', payload.fcmToken.substring(0, 20) + '...');
            } else if (payload.subscription && payload.subscription.endpoint) {
                // Legacy format - extract FCM token if possible
                const subscription = payload.subscription;
                if (subscription.endpoint && subscription.endpoint.includes('fcm.googleapis.com')) {
                    const fcmToken = subscription.endpoint.split('/').pop();
                    requestBody = {
                        fcmToken: fcmToken
                    };
                    console.log('📱 Extracted FCM token from legacy subscription:', fcmToken.substring(0, 20) + '...');
                } else {
                    throw new Error('Invalid subscription format - FCM token required. Endpoint must be from fcm.googleapis.com');
                }
            } else {
                throw new Error('Invalid payload - fcmToken is required');
            }

            console.log('🔄 Sending request to backend:', requestBody);

            const response = await axios.post(`/v2/push-notifications/subscribe`, requestBody, config);

            if (response.data.success) {
                commit('SET_SUBSCRIPTION_STATUS', {
                    status: 'subscribed',
                    endpointArn: response.data.endpointArn || response.data.subscriptionId,
                    platform: response.data.platform || 'fcm-web'
                });

                dispatch('notifications/addNotification', {
                    type: 'success',
                    message: 'Successfully subscribed to FCM push notifications'
                }, {root: true});

                console.log('✅ Backend registration successful:', response.data);
                return response.data;
            } else {
                throw new Error(response.data.message || 'Failed to subscribe');
            }
        }
        catch (err) {
            console.error('Error subscribing to push notifications:', err);
            commit('SET_SUBSCRIPTION_STATUS', {
                status: 'error',
                endpointArn: null,
                platform: null
            });

            dispatch('notifications/addNotification', {
                type: 'error',
                message: 'Failed to subscribe to push notifications'
            }, {root: true});
            throw err;
        }
    },

    async unsubscribeFromPushNotifications({commit, dispatch, rootState}, payload) {
        // Try to refresh the token first to ensure we have a valid token
        try {
            await getAuthToken();
        } catch (error) {
            console.error('Failed to refresh token before unsubscribing:', error);
            // Continue anyway and let the API call handle any auth issues
        }

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            };

            // If payload contains fcmToken, send it for specific unsubscription
            const requestData = payload?.fcmToken ? { fcmToken: payload.fcmToken } : {};

            const response = await axios.delete(`/v2/push-notifications/unsubscribe`, {
                ...config,
                data: requestData
            });

            if (response.data.success) {
                // Only clear local state if this was the last subscription
                if (response.data.removed === 1 || !payload?.fcmToken) {
                    commit('SET_SUBSCRIPTION_STATUS', {
                        status: 'unsubscribed',
                        endpointArn: null,
                        platform: null
                    });
                }

                dispatch('notifications/addNotification', {
                    type: 'success',
                    message: response.data.message || 'Successfully unsubscribed from push notifications'
                }, {root: true});

                return response.data;
            } else {
                throw new Error(response.data.message || 'Failed to unsubscribe');
            }
        }
        catch (err) {
            console.error('Error unsubscribing from push notifications:', err);
            dispatch('notifications/addNotification', {
                type: 'error',
                message: err.response?.data?.message || 'Failed to unsubscribe from push notifications'
            }, {root: true});
            throw err;
        }
    },

    async unsubscribeAllFromPushNotifications({commit, dispatch, rootState}) {
        // Try to refresh the token first to ensure we have a valid token
        try {
            await getAuthToken();
        } catch (error) {
            console.error('Failed to refresh token before unsubscribing all:', error);
            // Continue anyway and let the API call handle any auth issues
        }

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            };

            const response = await axios.post(`/v2/push-notifications/unsubscribe-all`, {}, config);

            if (response.data.success) {
                commit('SET_SUBSCRIPTION_STATUS', {
                    status: 'unsubscribed',
                    endpointArn: null,
                    platform: null
                });

                dispatch('notifications/addNotification', {
                    type: 'success',
                    message: response.data.message || 'Successfully unsubscribed from all push notifications'
                }, {root: true});

                return response.data;
            } else {
                throw new Error(response.data.message || 'Failed to unsubscribe all');
            }
        }
        catch (err) {
            console.error('Error unsubscribing from all push notifications:', err);
            dispatch('notifications/addNotification', {
                type: 'error',
                message: err.response?.data?.message || 'Failed to unsubscribe from all push notifications'
            }, {root: true});
            throw err;
        }
    },

    async sendTestNotification({commit, dispatch, rootState}, payload) {
        // Try to refresh the token first to ensure we have a valid token
        try {
            await getAuthToken();
        } catch (error) {
            console.error('Failed to refresh token before sending test notification:', error);
            // Continue anyway and let the API call handle any auth issues
        }

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            };

            const response = await axios.post(`/v2/push-notifications/test-fcm`, { platform: 'fcm-web' }, config);

            if (response.data.success) {
                dispatch('notifications/addNotification', {
                    type: 'success',
                    message: 'Test notification sent! Check your device.'
                }, {root: true});

                return response.data;
            } else {
                throw new Error(response.data.message || 'Failed to send test notification');
            }
        }
        catch (err) {
            console.error('Error sending test notification:', err);
            dispatch('notifications/addNotification', {
                type: 'error',
                message: 'Failed to send test notification'
            }, {root: true});
            throw err;
        }
    },

    // Reset state
    resetPushNotificationState({commit}) {
        commit('RESET_STATE');
    }
}

const mutations = {
    SET_SUBSCRIPTION_STATUS: (state, { status, endpointArn, platform }) => {
        state.subscriptionStatus = status;
        state.endpointArn = endpointArn;
        state.platform = platform;
    },

    SET_CONFIGURATION_STATUS: (state, { isConfigured, issues, platforms }) => {
        state.isConfigured = isConfigured;
        state.configurationIssues = issues;
        // You could store platforms info if needed
    },

    RESET_STATE: (state) => {
        Object.assign(state, getDefaultState());
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
} 